import { ofetch } from 'ofetch';
import { log } from '@shared/log.browser.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import { type TileListPageSession, createPageSession } from '@tilelist/application/core/TileListSession.ts';
import type { ClientReloadContextProvider } from '@tilelist/application/ports/ClientReloadContextProvider.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import { EvergladesClient } from '@tilelist/clients/EvergladesClient.ts';
import { WidgetSpinnerClient } from '@tilelist/components/promo-widget-spinner/WidgetSpinnerClient.ts';
import { FullTileListStrategy } from '@tilelist/domain/composition/strategies/FullTileListStrategy.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import type { OnEx } from '@tilelist/domain/experiments/OnEx.ts';
import { collectFeatureTracking } from '@tilelist/domain/tracking/ExperimentTracking.ts';
import { createCacheKeyRefresher } from '@tilelist/services/cache/CacheKeyRefresher.browser.ts';
import { restoreScrollPosition, updateScrollPosition } from '@tilelist/services/cache/ScrollPositionCache.browser.ts';
import { maybeRestoreFromCache, saveToCache } from '@tilelist/services/cache/TileListCache.browser.svelte.ts';
import { createEventsIncoming } from '@tilelist/services/events/EventsIncoming.browser.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import { getTrackingService, getVariationLoadingService } from '@tilelist/services/ServiceFactory.browser.ts';
import { trackAdjustForTileList } from '@tilelist/services/tracking/AppsTracking.browser.ts';
import { trackFirebase } from '@tilelist/services/tracking/FirebaseTracking.browser.ts';
import { getPageViewTracking } from '@tilelist/services/tracking/PageViewTracking.browser.ts';
import { createTrackingClientReloadContextProvider } from '@tilelist/services/TrackingClientReloadContextProvider.browser.ts';

export type StrategyFactory = (variationLoader: VariationLoader) => PageLoadStrategy;

export type TileListBrowserWiring = {
    session: TileListPageSession;
    eventsIncoming: EventsIncoming;
    eventsOutgoing: EventsOutgoing;
    trackingService: TrackingPort;
    variationLoadingService: VariationLoader;
    pageFetcher: TileListPageFetcher;
    reloadContextProvider: ClientReloadContextProvider;
    widgetSpinnerClient: WidgetSpinnerClient;
    restoreScrollPosition(): Promise<void>;
    maybeRestoreFromCache(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    sendTracking(pageView: TileListPageLayout, historic?: boolean): void;
    restoreAndTrackInitialPage(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    onRedirectToPdp(item: TileItem, currentPageView: TileListPageLayout | null): void;
};

/**
 *
 *
 *
 *
 *
 */
export function createTileListBrowserWiring(
    initialSession: TileListInitialSession,
    onex: OnEx,
    strategyFactory?: StrategyFactory,
): TileListBrowserWiring {
    const trackingService = getTrackingService();
    const variationLoadingService = getVariationLoadingService(onex);
    const eventsOutgoing = createEventsOutgoing();
    const eventsIncoming = createEventsIncoming();
    const reloadContextProvider = createTrackingClientReloadContextProvider(trackingService);

    /*                                                        */
    /*                                             */
    /*                              */
    /*                     */
    /*                                                    */
    /*                                                             */

    const evergladesClient = new EvergladesClient({
        fetchClient: ofetch,
        logger: log,
        entryRule: initialSession.session.entryRule,
        timeout: 10000,
    });

    const widgetSpinnerClient = new WidgetSpinnerClient({
        fetchClient: ofetch,
        logger: log,
        timeout: 3000,
    });

    const strategy = strategyFactory
        ? strategyFactory(variationLoadingService)
        : new FullTileListStrategy(variationLoadingService);

    const session = createPageSession(initialSession, {
        pageFetcher: evergladesClient,
        variationLoadingService,
        reloadContextProvider,
        eventPublisher: eventsOutgoing,
        cacheKeyRefresher: createCacheKeyRefresher(),
        strategy,
    });

    function sendTracking(pageView: TileListPageLayout, historic?: boolean): void {
        const { experimentIds, featureLabels } = collectFeatureTracking(pageView, session.onex);
        const pageViewTracking = getPageViewTracking(!!historic, experimentIds, featureLabels);
        trackingService.trackPageView(pageView, pageViewTracking);
        trackFirebase(eventsOutgoing, pageView.tileListItems);
        trackAdjustForTileList(pageView.tileListItems);
    }

    async function restoreAndTrackInitialPage(pageView: TileListPageLayout): Promise<TileListPageLayout> {
        const restored = await maybeRestoreFromCache(pageView);
        sendTracking(restored);
        return restored;
    }

    function onRedirectToPdp(item: TileItem, currentPageView: TileListPageLayout | null): void {
        if (initialSession.session.app.isIOSApp) {
            void updateScrollPosition(item);
        }
        if (currentPageView) {
            void saveToCache(currentPageView);
        }
    }

    return {
        session,
        eventsIncoming,
        eventsOutgoing,
        trackingService,
        variationLoadingService,
        pageFetcher: evergladesClient,
        reloadContextProvider,
        widgetSpinnerClient,
        restoreScrollPosition,
        maybeRestoreFromCache,
        sendTracking,
        restoreAndTrackInitialPage,
        onRedirectToPdp,
    };
}
